/**
 * **„Dein Strom vom Haus"** (HM-166) — die Karte aus Variante 3s/3q von
 * `ui_kits/dashboard/Kennzahlen Bewohner Varianten.html`: Ist gerade noch
 * PV-Strom frei, oder braucht die Wohnung mehr, als da ist?
 *
 * Oben die Antwort in Worten und Farbe (grün frei, gelb knapp, orange Netz),
 * darunter der Balken — der Strich ist das Verfügbare, Grün füllt sich mit dem
 * Verbrauch, was darüber hinausgeht, ist orange —, zuletzt zwei Zahlen.
 * Gerechnet hat der Server (`pvAvailability`), der Balken steht in
 * `lib/pv-availability.ts`.
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage.** Der Entwurf spricht von
 * „Ihrem Anteil" und zählt einen Hausakku dazu. Ein festes Kontingent je
 * Wohnung kennt keine Verteilregel: Frei ist, was das Haus gerade einspeist —
 * für jede Wohnung dieselbe Menge, und die Karte sagt das. **Seit HM-172 zählt
 * der Speicher mit**, wenn der Vermieter am Speicher eine Regel eingestellt hat
 * (`includesStorage`); Zahlen über Speicher und Erzeugung zeigt die Karte
 * bewusst nicht — nur das Ergebnis, und dann auch keinen erklärenden Satz.
 */
import { quantityColorVar } from "@hausmeter/ui";
import { formatPowerFull } from "../lib/energy-format.js";
import { availabilityBar, type PvAvailability } from "../lib/pv-availability.js";

const MONO = "var(--font-mono)";
const PV = quantityColorVar("unit_pv_share");
const GRID = quantityColorVar("unit_grid_share");

const HEADLINE: Record<PvAvailability["state"], { color: string; background: string }> = {
  spare: { color: PV, background: "var(--hm-green-50)" },
  tight: { color: "var(--hm-yellow-600)", background: "var(--hm-yellow-50)" },
  grid: { color: GRID, background: `color-mix(in srgb, ${GRID} 9%, var(--surface-card))` },
  no_sun: { color: "var(--text-strong)", background: "var(--surface-sunken)" },
};

function headline(value: PvAvailability): { title: string; text: string } {
  switch (value.state) {
    case "spare":
      return {
        title: `${formatPowerFull(value.spareW)} frei`,
        text: "Du kannst noch mehr einschalten.",
      };
    case "tight":
      return { title: "Gerade so", text: "Mehr einschalten holt Strom aus dem Netz." };
    case "grid":
      return {
        title: `${formatPowerFull(value.fromGridW)} aus dem Netz`,
        text: "Du brauchst mehr, als gerade da ist.",
      };
    case "no_sun":
      return { title: "Gerade keine Sonne", text: "Dein Strom kommt jetzt aus dem Netz." };
  }
}

export function PvAvailabilityCard({
  value,
  consumptionW,
}: {
  value: PvAvailability;
  consumptionW: number;
}) {
  const { title, text } = headline(value);
  const tone = HEADLINE[value.state];
  const bar = availabilityBar(value, consumptionW);
  return (
    <section
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minWidth: 0,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-muted)" }}>
          Dein Strom vom Haus
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            font: `600 14px ${MONO}`,
            color: "var(--text-strong)",
          }}
        >
          <span
            style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--hm-green-500)" }}
          />
          jetzt
        </span>
      </div>

      <div
        style={{
          padding: "12px 14px",
          borderRadius: "var(--radius-md)",
          background: tone.background,
        }}
      >
        <div style={{ font: "700 20px/1.2 var(--font-display)", color: tone.color }}>{title}</div>
        <div style={{ marginTop: 3, fontSize: 13, color: "var(--text-body)" }}>{text}</div>
      </div>

      {bar !== null && (
        <div
          role="img"
          aria-label={`Verfügbar ${formatPowerFull(value.availableW)}, du brauchst ${formatPowerFull(consumptionW)}`}
          style={{
            position: "relative",
            height: 22,
            borderRadius: 6,
            background: "var(--surface-sunken)",
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: "0 auto 0 0",
              width: `${bar.marker * 100}%`,
              borderRadius: 6,
              background: `color-mix(in srgb, ${PV} 22%, transparent)`,
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: "0 auto 0 0",
              width: `${bar.covered * 100}%`,
              borderRadius: bar.covered >= 1 && bar.fromGrid === 0 ? 6 : "6px 0 0 6px",
              background: PV,
            }}
          />
          {bar.fromGrid > 0 && (
            <span
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${bar.marker * 100}%`,
                width: `${bar.fromGrid * 100}%`,
                borderRadius: "0 6px 6px 0",
                background: GRID,
              }}
            />
          )}
          <span
            style={{
              position: "absolute",
              top: -5,
              bottom: -5,
              left: `calc(${bar.marker * 100}% - 1px)`,
              width: 2,
              borderRadius: 1,
              background: "var(--text-strong)",
            }}
          />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        <Figure label="Verfügbar für dich" w={value.availableW} />
        <Figure label="Du brauchst" w={consumptionW} />
      </div>

      {/* Mit Speicher steht kein Satz da (Auftraggeber, 0.68.1): Die Regel ist Sache des
          Vermieters, und erklärt wäre sie eine Auskunft über das Haus. */}
      {value.state === "spare" && !value.includesStorage && (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
          Frei ist, was das Haus gerade ins Netz einspeist — für jede Wohnung dieselbe Menge.
        </p>
      )}
    </section>
  );
}

function Figure({ label, w }: { label: string; w: number }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>{label}</div>
      <div
        style={{
          font: `600 17px ${MONO}`,
          fontVariantNumeric: "tabular-nums",
          color: "var(--text-strong)",
        }}
      >
        {formatPowerFull(w)}
      </div>
    </div>
  );
}
